import React, { useRef, useState, useEffect } from 'react'

import {
  RadioGroup,
  Grid,
  Row,
  Picker,
  Text,
  SimpleCard,
  CheckBox,
  SimpleCollapse,
  Input,
  Col,
  Button,
  UnidadeGestoraFields,
  Form
} from 'components'
import { MagnifyingGlassIcon } from 'components/Icons'
import { ReportsRow, getItemSize, buildSetSize } from 'components/ReportsRow'
import {
  useWindowResize,
  useLoading,
  useException,
  useModal,
  usePersistForm
} from 'hooks'
import moment from 'moment'
import { useForm } from 'react-hook-form'
import { VariableSizeList as List } from 'react-window'
import { IResponseTypes } from 'services/core/genericEntityGST'
import { DashboardServices } from 'services/GST/Dashboard'
import { SPACING } from 'utils'

import { useDashboard } from '../../context'
import { TGraphicType, TContentReportType } from '../../context/provider'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'

function validateDate(value: unknown) {
  const isInvalidDate = false
  const isValidDate = true

  if (value === null) {
    return isInvalidDate
  }

  if (moment(value).isValid()) {
    return isValidDate
  }

  return isInvalidDate
}

const SCHEMA = yup.object({
  matriculaContraparte: yup.string().nullable().notRequired(),
  nomeContraparte: yup.string().nullable().notRequired(),
  matriculaContraparteSubstituto: yup.string().nullable().notRequired(),
  nomeContraparteSubstituto: yup.string().nullable().notRequired(),
  nomeEmpresa: yup.string().nullable().notRequired(),
  nomeCentro: yup.string().nullable().notRequired(),
  nomeFornecedor: yup.string().nullable().notRequired(),
  periodoIni: yup
    .date()
    .required('Informe um período.')
    .typeError('Digite uma data válida.'),
  periodoFim: yup
    .date()
    .required('Informe um período.')
    .typeError('Digite uma data válida.')
    .min(yup.ref('periodoIni'), 'Data fim deve ser maior que data inicial.')
})

interface IReportForm {
  matriculaContraparte: string
  nomeContraparte: string
  matriculaContraparteSubstituto: string
  nomeContraparteSubstituto: string
  nomeEmpresa: string
  nomeCentro: string
  nomeFornecedor: string
  periodoIni: string
  periodoFim: string
}

const ReportForm: React.FC = () => {
  const { showModal } = useModal()
  const { setLoading } = useLoading()
  const {
    setError,
    clearErrors,
    formState: { errors },
    watch,
    setValue,
    control,
    handleSubmit,
    reset
  } = useForm<IReportForm>({
    resolver: yupResolver(SCHEMA),
    defaultValues: {
      matriculaContraparte: '',
      matriculaContraparteSubstituto: '',
      nomeCentro: '',
      nomeContraparte: '',
      nomeContraparteSubstituto: '',
      nomeEmpresa: '',
      nomeFornecedor: '',
      periodoFim: null,
      periodoIni: null
    }
  })

  usePersistForm('report-dashboard-form', {
    setValue: setValue,
    watch: watch
  })

  const form = watch()

  const { createCustomException, getException } = useException()
  const {
    setPeriodo,
    periodo,
    reportType,
    setReportType,
    setContentReportType,
    contentReportType,
    selectedItensOfGrid,
    lastInteractionGridName,
    handleChangeCheckAll,
    setLastInteractionGridName,
    handleChangeCheckbox,
    setUnidadeGestoraId,
    unidadeGestoraId,
    setViewGraphic,
    dashboardData,
    setDashboardData,
    isCheckAll,
    setSelectedItensOfGrid
  } = useDashboard()

  useEffect(() => {
    getDashboardData()
  }, [])

  const getDashboardData = () => {
    setLoading('loading')

    DashboardServices()
      .getDashboard({
        idCentroPlantas: selectedItensOfGrid.idCentroPlanta,
        idContraparteSubstitutos: selectedItensOfGrid.idContraparteSubstituto,
        idContrapartes: selectedItensOfGrid.idContraparte,
        idEmpresas: selectedItensOfGrid.idEmpresa,
        idFornecedors: selectedItensOfGrid.idFornecedor,
        idUnidadeGestora: unidadeGestoraId,
        matriculaContraparte: form.matriculaContraparte,
        matriculaContraparteSubstituto: form.matriculaContraparteSubstituto,
        nomeCentro: form.nomeCentro,
        nomeContraparte: form.nomeContraparte,
        nomeContraparteSubstituto: form.nomeContraparteSubstituto,
        nomeEmpresa: form.nomeEmpresa,
        nomeFornecedor: form.nomeFornecedor,
        periodoFim: form.periodoFim,
        periodoInicio: form.periodoIni
      })
      .then((response) => {
        if (response.status !== 200) {
          const { tipo, mensagem, detalhe } = response.data

          createCustomException({
            detalhe,
            mensagem,
            tipo: IResponseTypes[tipo]
          })
        }

        if (lastInteractionGridName) {
          Object.keys(response.data.entity).forEach((gridName: string) => {
            const gridData = response.data.entity[gridName]

            if (lastInteractionGridName !== gridName) {
              setDashboardData((prevState) => ({
                ...prevState,
                [gridName]: gridData
              }))
            }
          })

          return
        }

        response?.data?.entity && setDashboardData(response?.data?.entity)
      })
      .catch((err: Error) => {
        const error = getException(err)

        showModal({
          hideButtonCancel: true,
          message: error.mensagem,
          txtButtonConfirm: 'OK',
          type: error.tipo || 'error'
        })
      })
      .finally(() => setLoading('idle'))
  }

  const handleChangeContentType = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setContentReportType(event.target.value as TContentReportType)
  }

  const handleChangeReportType = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setReportType(event.target.value as TGraphicType)
  }

  const handleClear = () => {
    // Reset form
    reset()

    // Limpa itens selecionados
    setSelectedItensOfGrid({
      idCentroPlanta: [],
      idContraparte: [],
      idContraparteSubstituto: [],
      idEmpresa: [],
      idFornecedor: []
    })
    // Limpa datas
    setPeriodo({
      fim: null,
      inicio: null
    })
    // Limpa UnidadeGestoras
    unidadeGestoraRef?.current?.clearForm()
  }

  const handleVisualizacao = () => {
    if (!moment(form.periodoIni).isValid()) {
      if (form.periodoIni === null)
        setError('periodoIni', { message: 'Campo obrigatório', type: 'error' })
      else
        setError('periodoIni', {
          message: 'Insira uma data válida',
          type: 'error'
        })
    }

    if (!moment(form.periodoFim).isValid()) {
      if (form.periodoFim === null)
        setError('periodoFim', { message: 'Campo obrigatório', type: 'error' })
      else
        setError('periodoFim', {
          message: 'Insira uma data válida',
          type: 'error'
        })
    }
    setPeriodo({ fim: form.periodoFim, inicio: form.periodoIni })

    if (
      moment(form.periodoIni).isValid() &&
      moment(form.periodoFim).isValid()
    ) {
      setViewGraphic((prevState) => !prevState)
    }
  }

  useEffect(() => {
    getDashboardData()
  }, [selectedItensOfGrid, unidadeGestoraId, periodo])

  const unidadeGestoraRef = useRef(null)

  const contraparteSizeMap = useRef({})
  const contraparteListRef = useRef(null)
  const contraparteSetSize = buildSetSize(
    contraparteSizeMap,
    contraparteListRef
  )

  const contraparteSubstitutoSizeMap = useRef({})
  const contraparteSubstitutoListRef = useRef(null)
  const contraparteSubstitutoSetSize = buildSetSize(
    contraparteSubstitutoSizeMap,
    contraparteSubstitutoListRef
  )

  const empresasSizeMap = useRef({})
  const empresasListRef = useRef(null)
  const empresasSetSize = buildSetSize(empresasSizeMap, empresasListRef)

  const centrosSizeMap = useRef({})
  const centrosListRef = useRef(null)
  const centrosSetSize = buildSetSize(centrosSizeMap, centrosListRef)

  const fornecedoresSizeMap = useRef({})
  const fornecedoresListRef = useRef(null)
  const fornecedoresSetSize = buildSetSize(
    fornecedoresSizeMap,
    fornecedoresListRef
  )

  const [windowWidth] = useWindowResize()

  useEffect(() => {
    if (validateDate(periodo.inicio) || periodo.inicio === null)
      clearErrors('periodoIni')
    if (validateDate(periodo.fim) || periodo.fim === null)
      clearErrors('periodoFim')
  }, [periodo])

  return (
    <>
      <Grid
        gridCols="1fr 1fr 0.5fr 1fr"
        gridGap={`0 ${SPACING.spacing20}`}
        margin={`${SPACING.spacing10} 0 ${SPACING.spacing10} 0`}
        alignItems="flex-start"
      >
        <RadioGroup
          label="Dashboard"
          onChange={handleChangeContentType}
          row
          value={contentReportType}
          listOptions={[
            { label: 'Geral', value: 'geral' },
            { label: 'Itens Pendentes', value: 'itens' }
          ]}
        />
        <RadioGroup
          label="Tipos de Gráfico"
          onChange={handleChangeReportType}
          row
          value={reportType}
          listOptions={[
            { label: 'Circular', value: 'circular' },
            { label: 'Linear', value: 'linear' }
          ]}
        />
        <div />
        <Row alignItems="flex-start">
          <Picker
            maxDate={periodo.fim}
            onChange={(date) => {
              setPeriodo((prevState) => ({
                ...prevState,
                inicio: date
              }))
            }}
            width="140px"
            defaultValue={null}
            label="Período"
            value={periodo.inicio}
            errors={errors}
            formValue="periodoIni"
            control={control}
          />
          <Text
            marginleft={SPACING.spacing10}
            marginright={SPACING.spacing10}
            margintop={SPACING.spacing30}
          >
            à
          </Text>
          <Picker
            minDate={periodo.inicio}
            width="140px"
            onChange={(date) => {
              setPeriodo((prevState) => ({
                ...prevState,
                fim: date
              }))
            }}
            defaultValue={null}
            label=""
            value={periodo.fim}
            errors={errors}
            formValue="periodoFim"
            control={control}
          />
        </Row>
      </Grid>

      <SimpleCollapse isExpanded title="INFORMAÇÕES GERAIS">
        <Grid
          gridCols="1fr 1fr"
          gridGap={`0 ${SPACING.spacing20}`}
          margin={`${SPACING.spacing10} 0 ${SPACING.spacing10} 0`}
          alignItems="flex-start"
        >
          <Grid gridCols="1fr" gridGap={`0 ${SPACING.spacing20}`}>
            <Grid
              gridCols="1fr 1fr"
              gridGap={`0 ${SPACING.spacing20}`}
              margin={`${SPACING.spacing10} 0 ${SPACING.spacing10} 0`}
            >
              <Input
                label="Matrícula Contraparte"
                placeholder="N°"
                formValue="matriculaContraparte"
                control={control}
                inputIcon={<MagnifyingGlassIcon />}
                onClickIcon={handleSubmit(getDashboardData)}
              />
              <Input
                label="Contraparte"
                placeholder="Nome do Contratante"
                formValue="nomeContraparte"
                control={control}
                inputIcon={<MagnifyingGlassIcon />}
                onClickIcon={handleSubmit(getDashboardData)}
              />
            </Grid>
            <Row alignItems="flex-start">
              <CheckBox
                label="Marcar Todos"
                disabled={dashboardData?.listaContrapartes?.length === 0}
                checked={isCheckAll('idContraparte')}
                onChange={(e) =>
                  handleChangeCheckAll({
                    checkListName: 'idContraparte',
                    checked: e.target.checked
                  })
                }
              />
            </Row>
            <Grid
              gridCols="1fr"
              gridGap={`0 ${SPACING.spacing20}`}
              margin={`${SPACING.spacing10} 0 ${SPACING.spacing10} 0`}
            >
              <SimpleCard padding="0" minHeight="300px" maxHeight="300px">
                {dashboardData?.listaContrapartes && (
                  <List
                    itemCount={dashboardData?.listaContrapartes?.length}
                    estimatedItemSize={30}
                    itemSize={(index: number) =>
                      getItemSize(contraparteSizeMap, index)
                    }
                    ref={contraparteListRef}
                    height={300}
                    width="100%"
                    useIsScrolling
                  >
                    {({ index, style }) => (
                      <>
                        <div
                          style={style}
                          key={dashboardData?.listaContrapartes[index]?.id}
                        >
                          <ReportsRow
                            index={index}
                            windowWidth={windowWidth}
                            setSize={contraparteSetSize}
                          >
                            <CheckBox
                              onChange={() =>
                                handleChangeCheckbox({
                                  checkListName: 'idContraparte',
                                  itemId:
                                    dashboardData?.listaContrapartes[index]?.id
                                })
                              }
                              checked={selectedItensOfGrid.idContraparte.includes(
                                dashboardData?.listaContrapartes[index]?.id
                              )}
                              key={dashboardData?.listaContrapartes[index]?.id}
                              label={
                                dashboardData?.listaContrapartes[index]
                                  ?.nomeContraparte
                              }
                            />
                          </ReportsRow>
                        </div>
                      </>
                    )}
                  </List>
                )}
              </SimpleCard>
            </Grid>
          </Grid>
          <Grid gridCols="1fr" gridGap={`0 ${SPACING.spacing20}`}>
            <Grid
              gridCols="1fr 1fr"
              gridGap={`0 ${SPACING.spacing20}`}
              margin={`${SPACING.spacing10} 0 ${SPACING.spacing10} 0`}
            >
              <Input
                label="Matrícula do Substituto"
                placeholder="N°"
                formValue="matriculaContraparteSubstituto"
                control={control}
                inputIcon={<MagnifyingGlassIcon />}
                onClickIcon={handleSubmit(getDashboardData)}
              />
              <Input
                label="Nome do Substituto"
                placeholder="Nome do Substituto"
                formValue="nomeContraparteSubstituto"
                control={control}
                inputIcon={<MagnifyingGlassIcon />}
                onClickIcon={handleSubmit(getDashboardData)}
              />
            </Grid>
            <Row alignItems="flex-start">
              <CheckBox
                label="Marcar Todos"
                checked={isCheckAll('idContraparteSubstituto')}
                disabled={
                  dashboardData.listaContrapartesSubstituto.length === 0
                }
                onChange={(e) =>
                  handleChangeCheckAll({
                    checkListName: 'idContraparteSubstituto',
                    checked: e.target.checked
                  })
                }
              />
            </Row>
            <Grid
              gridCols="1fr"
              gridGap={`0 ${SPACING.spacing20}`}
              margin={`${SPACING.spacing10} 0 ${SPACING.spacing10} 0`}
            >
              <SimpleCard padding="0" minHeight="300px" maxHeight="300px">
                {dashboardData?.listaContrapartesSubstituto && (
                  <List
                    itemCount={dashboardData.listaContrapartesSubstituto.length}
                    estimatedItemSize={30}
                    itemSize={(index: number) =>
                      getItemSize(contraparteSubstitutoSizeMap, index)
                    }
                    ref={contraparteSubstitutoListRef}
                    height={300}
                    width="100%"
                    useIsScrolling
                  >
                    {({ index, style }) => (
                      <>
                        <div
                          style={style}
                          key={
                            dashboardData.listaContrapartesSubstituto[index].id
                          }
                        >
                          <ReportsRow
                            index={index}
                            windowWidth={windowWidth}
                            setSize={contraparteSubstitutoSetSize}
                          >
                            <CheckBox
                              onChange={() =>
                                handleChangeCheckbox({
                                  checkListName: 'idContraparteSubstituto',
                                  itemId:
                                    dashboardData.listaContrapartesSubstituto[
                                      index
                                    ].id
                                })
                              }
                              checked={selectedItensOfGrid.idContraparteSubstituto.includes(
                                dashboardData.listaContrapartesSubstituto[index]
                                  .id
                              )}
                              key={
                                dashboardData.listaContrapartesSubstituto[index]
                                  .id
                              }
                              label={
                                dashboardData.listaContrapartesSubstituto[index]
                                  .nomeContraparteSubstituto
                              }
                            />
                          </ReportsRow>
                        </div>
                      </>
                    )}
                  </List>
                )}
              </SimpleCard>
            </Grid>
          </Grid>
        </Grid>
        <Grid
          gridCols="1fr 1fr 1fr"
          gridGap={`0 ${SPACING.spacing20}`}
          margin={`${SPACING.spacing10} 0 ${SPACING.spacing10} 0`}
          alignItems="flex-start"
        >
          <Input
            placeholder="Nome da Empresa"
            label="Empresa"
            formValue="nomeEmpresa"
            control={control}
            inputIcon={<MagnifyingGlassIcon />}
            onClickIcon={handleSubmit(getDashboardData)}
          />
          <Input
            placeholder="Nome do Centro"
            label="Centro"
            formValue="nomeCentro"
            control={control}
            inputIcon={<MagnifyingGlassIcon />}
            onClickIcon={handleSubmit(getDashboardData)}
          />
          <Input
            placeholder="Nome do Fornecedor"
            label="Fornecedor"
            formValue="nomeFornecedor"
            control={control}
            inputIcon={<MagnifyingGlassIcon />}
            onClickIcon={handleSubmit(getDashboardData)}
          />
        </Grid>
        <Grid
          gridCols="1fr 1fr 1fr"
          gridGap={`0 ${SPACING.spacing20}`}
          margin={`${SPACING.spacing10} 0 ${SPACING.spacing10} 0`}
          alignItems="flex-start"
        >
          <Col>
            <CheckBox
              label="Marcar Todos"
              checked={isCheckAll('idEmpresa')}
              disabled={dashboardData.listaEmpresas.length === 0}
              onChange={(e) =>
                handleChangeCheckAll({
                  checkListName: 'idEmpresa',
                  checked: e.target.checked
                })
              }
            />
            <SimpleCard
              padding="0"
              minHeight="300px"
              maxHeight="300px"
              marginTop={SPACING.spacing10}
            >
              {dashboardData?.listaEmpresas && (
                <List
                  itemCount={dashboardData.listaEmpresas.length}
                  estimatedItemSize={30}
                  itemSize={(index: number) =>
                    getItemSize(empresasSizeMap, index)
                  }
                  ref={empresasListRef}
                  height={300}
                  width="100%"
                  useIsScrolling
                >
                  {({ index, style }) => (
                    <>
                      <div
                        style={style}
                        key={dashboardData.listaEmpresas[index].id}
                      >
                        <ReportsRow
                          index={index}
                          windowWidth={windowWidth}
                          setSize={empresasSetSize}
                        >
                          <CheckBox
                            onChange={() =>
                              handleChangeCheckbox({
                                checkListName: 'idEmpresa',
                                itemId: dashboardData.listaEmpresas[index].id
                              })
                            }
                            checked={selectedItensOfGrid.idEmpresa.includes(
                              dashboardData.listaEmpresas[index].id
                            )}
                            key={dashboardData.listaEmpresas[index].id}
                            label={
                              dashboardData.listaEmpresas[index]
                                .descricaoEmpresa
                            }
                          />
                        </ReportsRow>
                      </div>
                    </>
                  )}
                </List>
              )}
            </SimpleCard>
          </Col>
          <Col>
            <CheckBox
              label="Marcar Todos"
              checked={isCheckAll('idCentroPlanta')}
              disabled={dashboardData.listaCentros.length === 0}
              onChange={(e) =>
                handleChangeCheckAll({
                  checkListName: 'idCentroPlanta',
                  checked: e.target.checked
                })
              }
            />
            <SimpleCard
              padding="0"
              minHeight="300px"
              maxHeight="300px"
              marginTop={SPACING.spacing10}
            >
              {dashboardData?.listaCentros && (
                <List
                  itemCount={dashboardData.listaCentros.length}
                  estimatedItemSize={30}
                  itemSize={(index: number) =>
                    getItemSize(centrosSizeMap, index)
                  }
                  ref={centrosListRef}
                  height={300}
                  width="100%"
                  useIsScrolling
                >
                  {({ index, style }) => (
                    <>
                      <div
                        style={style}
                        key={dashboardData.listaCentros[index].id}
                      >
                        <ReportsRow
                          index={index}
                          windowWidth={windowWidth}
                          setSize={centrosSetSize}
                        >
                          <CheckBox
                            onChange={() =>
                              handleChangeCheckbox({
                                checkListName: 'idCentroPlanta',
                                itemId: dashboardData.listaCentros[index].id
                              })
                            }
                            checked={selectedItensOfGrid.idCentroPlanta.includes(
                              dashboardData.listaCentros[index].id
                            )}
                            key={dashboardData.listaCentros[index].id}
                            label={
                              dashboardData.listaCentros[index]
                                .descricaoCentroPlanta
                            }
                          />
                        </ReportsRow>
                      </div>
                    </>
                  )}
                </List>
              )}
            </SimpleCard>
          </Col>
          <Col>
            <CheckBox
              label="Marcar Todos"
              checked={isCheckAll('idFornecedor')}
              disabled={dashboardData.listaFornecedores.length === 0}
              onChange={(e) =>
                handleChangeCheckAll({
                  checkListName: 'idFornecedor',
                  checked: e.target.checked
                })
              }
            />
            <SimpleCard
              padding="0"
              minHeight="300px"
              maxHeight="300px"
              marginTop={SPACING.spacing10}
            >
              {dashboardData?.listaFornecedores && (
                <List
                  itemCount={dashboardData.listaFornecedores.length}
                  estimatedItemSize={30}
                  itemSize={(index: number) =>
                    getItemSize(fornecedoresSizeMap, index)
                  }
                  ref={fornecedoresListRef}
                  height={300}
                  width="100%"
                  useIsScrolling
                >
                  {({ index, style }) => (
                    <>
                      <div
                        style={style}
                        key={dashboardData.listaFornecedores[index].id}
                      >
                        <ReportsRow
                          index={index}
                          windowWidth={windowWidth}
                          setSize={fornecedoresSetSize}
                        >
                          <CheckBox
                            onChange={() =>
                              handleChangeCheckbox({
                                checkListName: 'idFornecedor',
                                itemId:
                                  dashboardData.listaFornecedores[index].id
                              })
                            }
                            checked={selectedItensOfGrid.idFornecedor.includes(
                              dashboardData.listaFornecedores[index].id
                            )}
                            key={dashboardData.listaFornecedores[index].id}
                            label={
                              dashboardData.listaFornecedores[index]
                                .nomeFornecedor
                            }
                          />
                        </ReportsRow>
                      </div>
                    </>
                  )}
                </List>
              )}
            </SimpleCard>
          </Col>
        </Grid>
        <Grid
          gridCols="1fr 1fr"
          gridGap={`0 ${SPACING.spacing20}`}
          margin={`${SPACING.spacing10} 0 ${SPACING.spacing10} 0`}
          alignItems="flex-start"
        >
          <Row marginTop={SPACING.spacing20} justifyContent="flex-end">
            <UnidadeGestoraFields
              ref={unidadeGestoraRef}
              gridCols={{
                Diretoria: 6,
                DiretoriaExecutiva: 6,
                Gerencia: 6,
                GerenciaGeral: 6,
                spacing: 2
              }}
              onChange={setUnidadeGestoraId}
            />
          </Row>
        </Grid>
        <Row marginTop={SPACING.spacing20} justifyContent="flex-end">
          <Button
            text="LIMPAR"
            variantButton="text"
            marginRight={SPACING.spacing10}
            onClick={handleClear}
          />
          <Button
            text="IMPRIMIR"
            variantButton="outlined"
            onClick={handleSubmit(handleVisualizacao)}
          />
        </Row>
      </SimpleCollapse>
    </>
  )
}
export default ReportForm
